import React, { useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import cn from '@/utils/cn';
import SPAButton from '../shared/SPAButton';
import { programLessonsPath, useContentMount } from './contentMount';
import { programEditPath } from './ProgramEdit/editSections';
import type { Program } from './useContentPrograms';

const columnHelper = createColumnHelper<Program>();

const linkClass = 'tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover tw-no-underline tw-transition-colors';

// The lessons page is routed on both mounts, so the title is a router link on each.
const TitleCell = ({ program }: { program: Program }) => (
  <Link className={linkClass} to={programLessonsPath(program.id, useContentMount())}>{program.title}</Link>
);

// The edit page is routed on both mounts (ContentView, admin/ProgramsPage), so Edit never reloads.
const EditCell = ({ program }: { program: Program }) => (
  <SPAButton asChild size="xs" variant="outline">
    <Link to={programEditPath(program.id, useContentMount())}>Edit</Link>
  </SPAButton>
);

const SubscribersCell = ({ subscribers }: { subscribers: string[] }) => {
  const [expanded, setExpanded] = useState(false);
  const count = subscribers.length;

  return (
    <div>
      <span>{count} {count === 1 ? 'subscriber' : 'subscribers'}</span>
      {count > 0 && (
        <button
          aria-expanded={expanded}
          className={cn(
            'tw-ml-2 tw-text-xs tw-bg-transparent tw-border-0 tw-cursor-pointer',
            'tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover',
          )}
          type="button"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Hide All' : 'View All'}
        </button>
      )}
      {expanded && (
        <div className="tw-mt-1 tw-text-xs tw-text-spa-secondary">
          {subscribers.join(', ')}
        </div>
      )}
    </div>
  );
};

const subscribersColumn = columnHelper.accessor('subscribers', {
  header: 'Subscribers',
  cell: info => <SubscribersCell subscribers={info.getValue()} />,
  size: 90,
  enableSorting: false,
});

// AIDEV-NOTE: Column definitions for @tanstack/react-table.
// License count cell links to the routed license availability modal.
export const getStandardColumns = (): ColumnDef<Program, unknown>[] => [
  columnHelper.accessor('title', {
    header: 'Title',
    cell: info => <TitleCell program={info.row.original} />,
    size: 400,
  }),
  columnHelper.accessor('copyrights', {
    header: 'Copyrights',
    cell: info => <span>&copy; {info.getValue()}</span>,
    size: 90,
  }),
  columnHelper.accessor('code', {
    header: 'Code',
    cell: info => <span className="tw-break-all">{info.getValue()}</span>,
    size: 155,
  }),
  columnHelper.accessor('numLicenses', {
    header: 'Licenses',
    cell: (info) => {
      const program = info.row.original;
      const count = info.getValue();

      if (count === 0) return <span>{count}</span>;

      // Relative to the Programs page route, so it resolves on both the SPA and legacy mounts.
      return (
        <Link
          className={linkClass}
          to={`programs/${program.id}/licenses`}
        >
          {count}
        </Link>
      );
    },
    size: 65,
  }),
  columnHelper.accessor('publishStatus', {
    header: 'Status',
    cell: info => <span>{info.getValue()}</span>,
    size: 75,
  }),
  columnHelper.display({
    id: 'action',
    header: 'Action',
    cell: info => <EditCell program={info.row.original} />,
    size: 60,
  }),
];

export const getCustomColumns = (): ColumnDef<Program, unknown>[] => {
  const standardCols = getStandardColumns();

  // Insert subscribers column after code (index 3)
  return [
    ...standardCols.slice(0, 3),
    subscribersColumn,
    ...standardCols.slice(3),
  ];
};
